import { checkIcon, FaOptionIcon } from '@europrocurement/l2d-icons';
import { alpha, Box, PaletteColor } from '@mui/material';
import React from 'react';
import { useL2DTheme } from '@europrocurement/flexy-components/hooks';

export type StepperStepExtendedProps = {
    name: string;
    label: string;
    index: number;
    done: boolean;
    disabled: boolean;
    layout?: 'horizontal' | 'vertical';
    onClick: (name: string) => void;
    color?: PaletteColor;
};

const ICON_SIZE = 20;

export const StepperStep: React.FunctionComponent<StepperStepExtendedProps> = function (
    props: StepperStepExtendedProps,
) {
    const { palette } = useL2DTheme();
    const { name, label, index, done, disabled, layout = 'horizontal', onClick, color } = props;
    const icon = <FaOptionIcon {...checkIcon.props} />;
    const mainColor = color || palette.primary;

    const wrapperSx = {
        display: 'flex',
        alignItems: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexDirection: layout === 'horizontal' ? 'row' : 'column',
        gap: layout === 'horizontal' ? '0 8px' : '8px 0',
    } as const;

    const iconSx = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: `${ICON_SIZE}px`,
        height: `${ICON_SIZE}px`,
        borderRadius: `${ICON_SIZE / 2}px`,
        fontFamily: 'Inter',
        fontSize: done ? '10px' : '12px',
        fontWeight: 700,
        lineHeight: '14.4px',
        textOverflow: 'clip',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        flexShrink: 0,
        backgroundColor: done ? mainColor.main : alpha(mainColor.main, 0.15),
        color: done ? palette.background.paper : mainColor.main,
        '& .MuiTypography-root': {
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
    };

    const labelSx = {
        fontFamily: 'Inter',
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: '25.2px',
        overflow: 'hidden',
        textOverflow: 'clip',
        whiteSpace: 'nowrap',
        color: palette.text.primary,
        opacity: disabled ? 0.5 : 1,
    };

    return (
        <Box
            data-testid="stepper-step"
            sx={wrapperSx}
            onClick={() => {
                if (!disabled) {
                    onClick(name);
                }
            }}
        >
            <Box sx={iconSx}>{done ? icon : index}</Box>
            <Box sx={labelSx}>{label}</Box>
        </Box>
    );
};
